import { AdminRole } from "./schema";

// Permission definitions for each action
export const permissions = {
  // App User Management (mobile app users)
  users: {
    view_list: ["super_admin", "admin"] as AdminRole[],
    view_profile: ["super_admin", "admin"] as AdminRole[],
    view_full_contact: ["super_admin"] as AdminRole[], // Admin sees masked data
    impersonate: ["super_admin"] as AdminRole[],
    suspend: ["super_admin", "admin"] as AdminRole[], // Admin can now suspend normal users
    restore: ["super_admin", "admin"] as AdminRole[], // Admin can now restore normal users
    force_logout: ["super_admin"] as AdminRole[],
    trigger_password_reset: ["super_admin"] as AdminRole[],
    grant_trial: ["super_admin"] as AdminRole[],
    refund: ["super_admin"] as AdminRole[],
    delete_gdpr: ["super_admin"] as AdminRole[],
    manage_subscriptions: ["super_admin", "admin"] as AdminRole[],
    add_support_notes: ["super_admin", "admin"] as AdminRole[],
    manage: ["super_admin"] as AdminRole[], // Manage internal portal users (Super Admin only)
  },

  influencer: {
    create: ["super_admin", "admin"] as AdminRole[],
    suspend: ["super_admin", "admin"] as AdminRole[], // also gates Restore on the Affiliates list
    delete_gdpr: ["super_admin"] as AdminRole[],
  },

  // Coach Management
  coaches: {
    view_list: ["super_admin", "admin", "coach_manager"] as AdminRole[],
    view_profile: ["super_admin", "admin", "coach_manager"] as AdminRole[],
    create: ["super_admin", "admin", "coach_manager"] as AdminRole[],
    update: ["super_admin", "admin", "coach_manager"] as AdminRole[],
    suspend: ["super_admin", "admin", "coach_manager"] as AdminRole[], // Coach manager can now suspend coaches
    restore: ["super_admin", "admin", "coach_manager"] as AdminRole[], // Coach manager can now restore coaches
    delete: ["super_admin"] as AdminRole[],
    trigger_password_reset: ["super_admin"] as AdminRole[],
    view_audit_history: ["super_admin"] as AdminRole[],
    approve_profile_changes: [
      "super_admin",
      "admin",
      "coach_manager",
    ] as AdminRole[],
    reject_profile_changes: [
      "super_admin",
      "admin",
      "coach_manager",
    ] as AdminRole[],
  },

  // Content Management
  content: {
    view: ["super_admin", "admin", "coach", "coach_manager"] as AdminRole[],
    view_all: ["super_admin", "admin"] as AdminRole[],
    view_own: ["super_admin", "admin", "coach", "coach_manager"] as AdminRole[],
    view_library: [
      "super_admin",
      "admin",
      "coach",
      "coach_manager",
    ] as AdminRole[],
    view_review_queue: ["super_admin", "admin"] as AdminRole[],
    create: ["super_admin", "admin", "coach"] as AdminRole[],
    edit: ["super_admin", "admin", "coach"] as AdminRole[],
    edit_draft: ["super_admin", "admin", "coach"] as AdminRole[],
    edit_any: ["super_admin", "admin"] as AdminRole[],
    edit_published: ["super_admin", "admin"] as AdminRole[],
    submit_for_review: ["super_admin", "admin", "coach"] as AdminRole[],
    withdraw: ["super_admin", "admin", "coach"] as AdminRole[],
    review: ["super_admin", "admin"] as AdminRole[],
    approve: ["super_admin", "admin"] as AdminRole[],
    request_changes: ["super_admin", "admin"] as AdminRole[],
    reject: ["super_admin", "admin"] as AdminRole[],
    publish: ["super_admin", "admin"] as AdminRole[],
    archive: ["super_admin", "admin"] as AdminRole[],
    delete: ["super_admin", "admin", "coach"] as AdminRole[],
    delete_any: ["super_admin", "admin"] as AdminRole[],
    manage_categories: ["super_admin", "admin"] as AdminRole[],
    schedule: ["super_admin", "admin"] as AdminRole[],
    view_version_history: ["super_admin", "admin", "coach"] as AdminRole[],
    rollback_version: ["super_admin"] as AdminRole[],
    view_metrics_all: ["super_admin", "admin"] as AdminRole[],
    view_metrics_own: ["super_admin", "admin", "coach"] as AdminRole[],
    view_internal_comments: ["super_admin", "admin"] as AdminRole[],
    duplicate: ["super_admin", "admin", "coach"] as AdminRole[],
  },

  // Notifications
  notifications: {
    send_to_all: ["super_admin"] as AdminRole[],
    send_to_segment: ["super_admin"] as AdminRole[],
    send_to_individual: ["super_admin", "admin"] as AdminRole[],
    send_to_coach: ["super_admin", "admin", "coach_manager"] as AdminRole[],
    view_user_notifications: ["super_admin", "admin"] as AdminRole[],
    view_coach_notifications: [
      "super_admin",
      "admin",
      "coach_manager",
    ] as AdminRole[],
    view_audit_log: ["super_admin"] as AdminRole[],
    preview_notification: [
      "super_admin",
      "admin",
      "coach",
      "coach_manager",
    ] as AdminRole[],
    schedule_notification: ["super_admin"] as AdminRole[],
    cancel_notification: ["super_admin"] as AdminRole[],
  },

  // Analytics & Dashboards
  analytics: {
    view_dashboard: [
      "super_admin",
      "admin",
      "coach",
      "coach_manager",
    ] as AdminRole[],
    view_full: ["super_admin"] as AdminRole[],
    view_summary: [
      "super_admin",
      "admin",
      "coach",
      "coach_manager",
    ] as AdminRole[],
    view_content_metrics: ["super_admin", "admin", "coach"] as AdminRole[],
    export: ["super_admin"] as AdminRole[],
  },

  // System Health
  system: {
    view_health: ["super_admin", "admin"] as AdminRole[],
    view_sync_status: ["super_admin", "admin"] as AdminRole[],
    manage_roles: ["super_admin"] as AdminRole[],
  },

  // Audit Logs (role-based visibility)
  audit: {
    view_all: ["super_admin"] as AdminRole[],
    view_content_logs: ["super_admin", "coach"] as AdminRole[],
    view_user_logs: ["super_admin", "admin"] as AdminRole[],
    view_subscription_logs: ["super_admin", "admin"] as AdminRole[],
    view_user_action_history: ["super_admin", "admin"] as AdminRole[],
  },

  // GDPR & Compliance
  gdpr: {
    view_requests: ["super_admin"] as AdminRole[],
    request_export: ["super_admin"] as AdminRole[],
    process_export: ["super_admin"] as AdminRole[],
    download_export: ["super_admin"] as AdminRole[],
    request_deletion: ["super_admin"] as AdminRole[],
    approve_deletion: ["super_admin"] as AdminRole[],
    reject_deletion: ["super_admin"] as AdminRole[],
    process_deletion: ["super_admin"] as AdminRole[],
  },

  // Governance
  governance: {
    confirm_destructive_action: ["super_admin"] as AdminRole[],
    view_security_alerts: ["super_admin", "admin"] as AdminRole[],
    view_policy_blocks: ["super_admin"] as AdminRole[],
  },

  // Subscriptions - ONLY visible to super_admin and admin
  subscriptions: {
    view_dashboard: ["super_admin", "admin"] as AdminRole[],
    view_details: ["super_admin", "admin"] as AdminRole[],
    extend_trial: ["super_admin", "admin"] as AdminRole[],
    grant_complimentary: ["super_admin"] as AdminRole[],
    pause: ["super_admin", "admin"] as AdminRole[],
    resume: ["super_admin", "admin"] as AdminRole[],
    cancel: ["super_admin"] as AdminRole[],
    view_history: ["super_admin", "admin"] as AdminRole[],
  },
} as const;

// Get primary/highest-priority role from array (for display)
export function getPrimaryRole(roles: AdminRole[]): AdminRole | undefined {
  const order = [
    "super_admin",
    "admin",
    "coach_manager",
    "coach",
    "influencer",
  ];
  return roles.find((r) => order.includes(r)) ?? roles[0];
}

// Check if a role has a specific permission
export function hasPermission(
  roles: AdminRole[],
  category: keyof typeof permissions,
  action: string,
): boolean {
  const categoryPermissions = permissions[category] as Record<
    string,
    AdminRole[]
  >;
  const allowedRoles = categoryPermissions[action];
  if (!allowedRoles) return false;
  return allowedRoles.some((r) => roles.includes(r));
}

// Get all permissions for a role
export function getRolePermissions(
  roles: AdminRole[],
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const [category, actions] of Object.entries(permissions)) {
    const categoryActions: string[] = [];
    for (const [action, allowedRoles] of Object.entries(
      actions as Record<string, AdminRole[]>,
    )) {
      if (roles.some((r) => allowedRoles.includes(r))) {
        categoryActions.push(action);
      }
    }
    if (categoryActions.length > 0) {
      result[category] = categoryActions;
    }
  }

  return result;
}

// Role display names
export const roleDisplayNames: Record<AdminRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  coach: "Coach",
  coach_manager: "Coach Manager",
  influencer: "Influencer",
  content_manager: "Content Manager",
};

// Role descriptions
export const roleDescriptions: Record<AdminRole, string> = {
  super_admin: "Full system access with all administrative privileges",
  admin: "User support, billing, and operational tasks",
  coach: "Content creation, profile management, and personal metrics",
  coach_manager: "Coach onboarding, performance tracking, and support",
  influencer: "Audience growth, content promotion, and affiliate engagement",
  content_manager: "Content creation, review, and management",
};

// Navigation items visibility per role
export interface NavItem {
  title: string;
  url: string;
  icon: string;
  requiredRoles: AdminRole[];
}

export const navigationConfig: NavItem[] = [
  {
    title: "Dashboard",
    url: "/",
    icon: "LayoutDashboard",
    requiredRoles: ["super_admin", "admin", "coach", "coach_manager"],
  },
  {
    title: "Users",
    url: "/users",
    icon: "UserCircle",
    requiredRoles: ["super_admin", "admin"],
  },
  {
    title: "Affiliates",
    url: "/affiliates",
    icon: "Network",
    requiredRoles: ["super_admin", "admin"],
  },
  {
    title: "Coaches",
    url: "/coaches",
    icon: "Users",
    requiredRoles: ["super_admin", "admin", "coach_manager"],
  },
  {
    title: "Subscriptions",
    url: "/subscriptions",
    icon: "CreditCard",
    requiredRoles: ["super_admin", "admin"],
  },
  {
    title: "Content Studio",
    url: "/studio",
    icon: "Sparkles",
    requiredRoles: ["super_admin", "admin", "coach", "coach_manager"],
  },
  {
    title: "Content Operations",
    url: "/content-operations",
    icon: "ClipboardList",
    requiredRoles: ["super_admin", "admin", "coach", "coach_manager"],
  },
  // {
  //   title: "User Support",
  //   url: "/user-support",
  //   icon: "MessageSquare",
  //   requiredRoles: ["super_admin", "admin"],
  // },
  {
    title: "Internal Users",
    url: "/internal-users",
    icon: "UserCog",
    requiredRoles: ["super_admin", "admin"],
  },
  {
    title: "Audit Logs",
    url: "/audit",
    icon: "FileText",
    requiredRoles: ["super_admin"],
  },
  // {
  //   title: "GDPR Compliance",
  //   url: "/gdpr",
  //   icon: "Shield",
  //   requiredRoles: ["super_admin"],
  // },
  {
    title: "Notifications",
    url: "/notifications",
    icon: "Bell",
    requiredRoles: [
      "super_admin",
      "admin",
      // "coach",
      // "coach_manager",
    ],
  },
  {
    title: "Settings",
    url: "/settings",
    icon: "Settings",
    requiredRoles: ["super_admin", "admin", "coach", "coach_manager"],
  },
];

export function getNavigationForRole(roles: AdminRole[]): NavItem[] {
  return navigationConfig.filter((item) =>
    item.requiredRoles.some((r) => roles.includes(r)),
  );
}
